/**
 * Wizard step 3b — only after choosing folders: the address we fetch the site
 * from.
 *
 * In folder mode the site's own domain points at us, so pages can no longer be
 * fetched from it — we'd be asking ourselves. The proxy always fetches from
 * `website.origin`, so a folder site stores this address there and keeps the
 * domain visitors use in `website.public`.
 *
 * Asked every time folders are chosen, not only when the detect says one is
 * needed: the detect can't prove a site survives its own domain pointing at us,
 * and a wrong "not needed" ships a site that fetches itself. (The one live
 * self-service folder site, avareit, runs off origin.avareit.com.)
 *
 * The detect only tailors the hint. It is never prefilled: the detect knows the
 * platform, not this project's address, and a guessed one would quietly fetch
 * somebody else's site.
 */

import { useState } from 'react'

import { checkFetchOrigin, type FetchOriginCheck } from './fetch-origin-check'

/** Platforms that already give every site a second address, and where to find it. */
const PLATFORM_HINTS: Record<string, { example: string; where: string }> = {
	Vercel: { example: 'your-project.vercel.app', where: 'Vercel → your project → Settings → Domains' },
	Netlify: { example: 'your-site.netlify.app', where: 'Netlify → Site configuration → Domain management' },
	'GitHub Pages': { example: 'your-name.github.io', where: 'your repository → Settings → Pages' },
}

export function FetchOriginStep({
	host,
	siteOrigin,
	hosting,
	submitting,
	onBack,
	onUseSubdomains,
	onContinue,
}: {
	/** The site's domain, without scheme — for copy and the placeholder. */
	host: string
	/** The site's own address; the fetch address must differ from it. */
	siteOrigin: string
	/** `hosting` from the detect — tailors the hint only. */
	hosting: string
	submitting: boolean
	onBack: () => void
	onUseSubdomains: () => void
	onContinue: (fetchOrigin: string) => void
}) {
	const [value, setValue] = useState('')
	const check = checkFetchOrigin(value, siteOrigin)
	const platform = PLATFORM_HINTS[hosting]
	const example = platform?.example ?? `origin.${host}`
	const error = errorText(check, example)

	function submit() {
		if (check.ok && !submitting) {
			onContinue(check.origin)
		}
	}

	return (
		<div className="card flex flex-col gap-5 p-5 sm:p-8">
			<div>
				<div className="label-mono mb-2">Step 3 · Folders</div>
				<h2 className="font-sans text-[24px] sm:text-[28px] font-medium leading-tight text-ink">
					Where should we fetch your site from?
				</h2>
				<p className="mt-1 text-[14px] text-ink-soft">
					With folder addresses <span className="font-mono text-ink">{host}</span> points at us, so we can't
					fetch your pages from it anymore. We need a second address that still reaches your site — visitors
					never see it.
				</p>
			</div>

			<div className="rounded-nayda-sm border border-line bg-surface/50 px-4 py-3 text-[13px] text-ink-soft">
				{platform ? (
					<>
						Your site is on <span className="font-medium text-ink">{hosting}</span>, which already gives it
						one — it looks like <span className="font-mono text-ink">{platform.example}</span>. Find it in{' '}
						{platform.where}.
					</>
				) : (
					<>
						Add a record at your DNS provider that points at the server your site runs on today. Most people
						call it <span className="font-mono text-ink">origin.{host}</span>.
					</>
				)}
			</div>

			<label className="flex flex-col gap-1.5">
				<span className="label-mono">Address we fetch from</span>
				<input
					type="text"
					autoFocus
					inputMode="url"
					autoCapitalize="off"
					spellCheck={false}
					value={value}
					onChange={(e) => setValue(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							e.preventDefault()
							submit()
						}
					}}
					aria-invalid={!!error}
					placeholder={example}
					className={
						'field font-mono ' +
						(error ? 'border-[#B23B2A] focus:border-[#B23B2A] focus:ring-[#B23B2A]/30' : '')
					}
				/>
				{error ? (
					<span className="text-[12px] text-[#B23B2A]">{error}</span>
				) : (
					<span className="text-[11px] text-ink-mute font-mono">
						{check.ok ? `We'll fetch pages from ${check.origin}` : 'https:// is added for you'}
					</span>
				)}
			</label>

			<p className="rounded-nayda-sm border border-line bg-surface/50 px-4 py-3 text-[13px] text-ink-soft">
				Too much hassle? <span className="font-medium text-ink">Subdomains need none of this</span> — your
				domain keeps pointing at your server.{' '}
				<button
					type="button"
					onClick={onUseSubdomains}
					disabled={submitting}
					className="text-ink underline underline-offset-2 hover:text-ember"
				>
					Use subdomains instead
				</button>
			</p>

			<div className="flex flex-wrap gap-3">
				<button
					onClick={onBack}
					disabled={submitting}
					className="btn btn-ghost whitespace-nowrap"
				>
					← Back
				</button>
				<button
					onClick={submit}
					disabled={!check.ok || submitting}
					className="btn btn-accent whitespace-nowrap"
				>
					{submitting ? 'Setting up…' : 'Add site'}
				</button>
			</div>
		</div>
	)
}

/** Explain a bad value — but say nothing about a field that is simply still empty. */
function errorText(check: FetchOriginCheck, example: string): string | null {
	if (check.ok || check.reason === 'empty') {
		return null
	}
	if (check.reason === 'same-as-site') {
		return `That's your site's own domain — with folders it points at us. Use a different address, like ${example}.`
	}
	return `That doesn't look like an address — try something like ${example}.`
}
